/**
 * Emit hosts for the `evidence` analytics group — 30 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { trackLegacyEvent } from '@/lib/analytics/track-legacy-event';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type MessageAbortedEventProps,
  type MessageFailedEventProps,
  type MessageReceivedEventProps,
  type MessageSubmittedEventProps,
} from '@/models/analytics/features/chat';
import {
  type EvidenceChatAbortedEventProps,
  type EvidenceChatCompletedEventProps,
  type EvidenceChatErrorEventProps,
  type EvidenceChatSidebarCloseEventProps,
  type EvidenceChatSidebarOpenEventProps,
  type EvidenceChatSubmittedEventProps,
  type EvidenceClickPastSessionClickedEventProps,
  type EvidenceDeepLinkSignInClickedEventProps,
  type EvidenceDeepLinkSignUpClickedEventProps,
  type EvidenceDeleteChatClickedEventProps,
  type EvidenceJurisdictionChangedEventProps,
  type EvidenceLanguageChangedEventProps,
  type EvidenceNewChatClickedEventProps,
  type EvidenceNhsContactSalesSubmittedEventProps,
  type EvidenceOnboardingSavedEventProps,
  type EvidenceOnboardingViewedEventProps,
  type EvidenceProTrialButtonStartedEventProps,
  type EvidenceSaveChatClickedEventProps,
  type EvidenceSidebarClickedEventProps,
  type EvidenceSourcesListClickedEventProps,
  type EvidenceSourcesListCustomClickedEventProps,
  type EvidenceSourcesListLocalGuidelinesClickedEventProps,
  type EvidenceSourcesListPresetClickedEventProps,
  type EvidenceSourcesListWebSearchClickedEventProps,
  type EvidenceSpecialtyChangedEventProps,
  type EvidenceToastShownEventProps,
  type EvidenceToastViewClickedEventProps,
  type EvidenceUnsaveChatClickedEventProps,
} from '@/models/analytics/features/evidence';
import {
  SegmentEvidenceActionName,
  SegmentObjectName,
} from '@/models/analytics/segment-names';
import { getUtmParams } from '@/utils/utmTracking';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';

/**
 * @deprecated Use trackMessageAborted instead. This fires both legacy and new events for backward compatibility.
 */
export function trackEvidenceChatAborted(input: EvidenceChatAbortedEventProps) {
  // Legacy event (will be removed after analytics migration)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceChatAborted,
    props: input,
  });
  // New object_action event
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.MessageAborted,
    props: {
      source: 'evidence',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      duration_ms: input.duration_ms,
      query_length: input.query_length,
      chat_id: input.chat_id,
      session_id: input.session_id,
    } as MessageAbortedEventProps,
  });
}

/**
 * Evidence chat completion. Emits `message_received` with `source: 'evidence'`
 * only — the legacy `evidence_chat_completed` dual-write is gone (Snowplow-first
 * and never forwarded to Amplitude, so usage charts on that name collapsed).
 */
export function trackEvidenceChatCompleted(input: EvidenceChatCompletedEventProps) {
  const utmSource = getUtmParams().utm_source;
  track({
    event_name: TrackingEventName.MessageReceived,
    props: {
      source: 'evidence',
      state: 'completed',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      is_managed: input.is_managed,
      duration_ms: input.duration_ms,
      time_to_first_token_ms: input.time_to_first_token_ms,
      references_count: input.references_count,
      query_length: input.query_length,
      response_length: input.response_length,
      chat_id: input.chat_id,
      message_id: input.message_id,
      session_id: input.session_id,
      tavily_search_count: input.tavily_search_count,
      tools_used: input.tools_used,
      word_count: input.word_count,
      custom_utm_source: utmSource,
      entry_method: input.entry_method,
      chip_label: input.chip_label,
      chip_category: input.chip_category,
      origin: input.origin,
      surface: input.surface,
      queued: input.queued,
      queue_duration_ms: input.queue_duration_ms,
    } as MessageReceivedEventProps,
    options: { amplitude: false, segment: true, posthog: true },
  });
}

/**
 * @deprecated Use trackMessageFailed instead. This fires both legacy and new events for backward compatibility.
 */
export function trackEvidenceChatError(input: EvidenceChatErrorEventProps) {
  const truncatedErrorMessage = input.error_message?.slice(0, 500);
  // Legacy event (will be removed after analytics migration)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceChatError,
    props: {
      ...input,
      error_message: truncatedErrorMessage,
    },
  });
  // New object_action event
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.MessageFailed,
    props: {
      source: 'evidence',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      error_message: truncatedErrorMessage,
      error_code: input.error_code,
      duration_ms: input.duration_ms,
      query_length: input.query_length,
      chat_id: input.chat_id,
      session_id: input.session_id,
      error_type: input.error_type,
      http_status: input.http_status,
      block_type: input.block_type,
      attachment_reason: input.attachment_reason,
    } as MessageFailedEventProps,
  });
}

export function trackEvidenceChatSidebarClose(input: EvidenceChatSidebarCloseEventProps) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSidebarCloseClicked,
      label: 'evidence_sidebar_close',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
    },
  });

  // V2 — Amplitude migration (sidebarClose -> panel_closed)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PanelClosed,
    props: {
      panel_type: 'evidence_sidebar',
      trigger: 'manual',
    },
  });
}

export function trackEvidenceChatSidebarOpen(input: EvidenceChatSidebarOpenEventProps) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSidebarOpenClicked,
      label: 'evidence_sidebar_open',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
    },
  });

  // V2 — Amplitude migration (sidebarOpen -> panel_opened)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PanelOpened,
    props: {
      panel_type: 'evidence_sidebar',
      trigger: 'manual',
    },
  });
}

/**
 * Evidence chat submit. Emits `message_submitted` with `source: 'evidence'`
 * only — the legacy `evidence_chat_submitted` dual-write is gone.
 */
export function trackEvidenceChatSubmitted(input: EvidenceChatSubmittedEventProps) {
  track({
    event_name: TrackingEventName.MessageSubmitted,
    props: {
      source: 'evidence',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      is_managed: input.is_managed,
      query_length: input.query_length,
      sources_count: input.sources_count,
      sources: input.sources,
      selection_mode: input.selection_mode,
      preset_primary_evidence: input.preset_primary_evidence,
      preset_local_guidelines: input.preset_local_guidelines,
      preset_web_search: input.preset_web_search,
      chat_id: input.chat_id,
      message_id: input.message_id,
      session_id: input.session_id,
      session_stage: input.session_stage,
      entry_method: input.entry_method,
      word_count: input.word_count,
      chip_label: input.chip_label,
      chip_category: input.chip_category,
      origin: input.origin,
      surface: input.surface,
      queued: input.queued,
      queue_duration_ms: input.queue_duration_ms,
    } as MessageSubmittedEventProps,
    options: { amplitude: false, segment: true, posthog: true },
  });
}

export function trackEvidenceClickPastSessionClicked(
  input: EvidenceClickPastSessionClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidencePastSessionClicked,
      label: 'evidence_past_session',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      chat_id: input.chat_id,
    },
  });

  // V2 — Amplitude migration (pastSessionClicked -> evidence_clicked)
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceClicked,
    props: {
      click_action: 'past_session_clicked',
      chat_id: input.chat_id,
    },
  });
}

export function trackEvidenceDeepLinkSignInClicked(
  input: EvidenceDeepLinkSignInClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSignInClicked,
      label: 'evidence_sign_in',
      evidence_location: input.evidence_location,
    },
  });
}

export function trackEvidenceDeepLinkSignUpClicked(
  input: EvidenceDeepLinkSignUpClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSignUpClicked,
      label: 'evidence_sign_up',
      evidence_location: input.evidence_location,
    },
  });
}

export function trackEvidenceDeleteChatClicked(
  input: EvidenceDeleteChatClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceDeleteChatClicked,
      label: 'evidence_delete_chat',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      chat_id: input.chat_id,
    },
  });
}

export function trackEvidenceJurisdictionChanged(
  input: EvidenceJurisdictionChangedEventProps
) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceJurisdictionChanged,
    props: input,
  });
}

export function trackEvidenceLanguageChanged(input: EvidenceLanguageChangedEventProps) {
  track({
    event_name: TrackingEventName.EvidenceLanguageChanged,
    props: input,
  });
}

export function trackEvidenceNewChatClicked(input: EvidenceNewChatClickedEventProps) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceNewChatClicked,
      label: 'evidence_new_chat',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      click_source: input.click_source,
    },
  });
}

export function trackEvidenceNhsContactSalesSubmitted(
  input: EvidenceNhsContactSalesSubmittedEventProps
) {
  trackLegacyEvent({
    name: SegmentObjectName.Evidence,
    props: {
      action: SegmentEvidenceActionName.EvidenceNhsContactSalesSubmitted,
    },
  });

  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceNhsContactSalesSubmitted,
    props: input,
  });
}

export function trackEvidenceOnboardingDismissed() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceOnboardingDismissed,
    props: {},
  });
}

export function trackEvidenceOnboardingSaved(input: EvidenceOnboardingSavedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceOnboardingSaved,
    props: input,
  });
}

export function trackEvidenceOnboardingSkipped() {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceOnboardingSkipped,
    props: {},
  });
}

export function trackEvidenceOnboardingViewed(input: EvidenceOnboardingViewedEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceOnboardingViewed,
    props: input,
  });
}

export function trackEvidenceProTrialButtonStarted(
  input: EvidenceProTrialButtonStartedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceProTrialClicked,
      label: 'evidence_pro_trial',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
    },
  });
}

export function trackEvidenceSaveChatClicked(input: EvidenceSaveChatClickedEventProps) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSaveChatClicked,
      label: 'evidence_save_chat',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      chat_id: input.chat_id,
      click_source: input.click_source,
    },
  });
}

// Evidence
export function trackEvidenceSidebarClicked(input: EvidenceSidebarClickedEventProps) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSidebarClicked,
      label: 'evidence_sidebar',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
    },
  });
}

export function trackEvidenceSourcesListClicked(
  input: EvidenceSourcesListClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSourcesListClicked,
      label: 'evidence_sources_list',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
    },
  });
}

export function trackEvidenceSourcesListCustomClicked(
  input: EvidenceSourcesListCustomClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSourcesCustomClicked,
      label: 'evidence_sources_custom',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      custom_sources_count: input.custom_sources_count,
    },
  });
}

export function trackEvidenceSourcesListLocalGuidelinesClicked(
  input: EvidenceSourcesListLocalGuidelinesClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceLocalGuidelinesClicked,
      label: 'evidence_local_guidelines',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      is_enabled: input.is_enabled,
    },
  });
}

export function trackEvidenceSourcesListPresetClicked(
  input: EvidenceSourcesListPresetClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceSourcesPresetClicked,
      label: 'evidence_sources_preset',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      preset_type: input.preset_type,
      is_enabled: input.is_enabled,
    },
  });
}

export function trackEvidenceSourcesListWebSearchClicked(
  input: EvidenceSourcesListWebSearchClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceWebSearchClicked,
      label: 'evidence_web_search',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      is_enabled: input.is_enabled,
    },
  });
}

export function trackEvidenceSpecialtyChanged(input: EvidenceSpecialtyChangedEventProps) {
  track({
    event_name: TrackingEventName.EvidenceSpecialtyChanged,
    props: input,
  });

  // V2 — Amplitude migration (specialtyChanged -> settings_updated)
  track({
    event_name: TrackingEventName.SettingsUpdated,
    props: {
      update_action: 'specialty_changed',
      specialty_id: input.specialty_id,
    },
  });
}

export function trackEvidenceToastShown(input: EvidenceToastShownEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.EvidenceToastShown,
    props: input,
  });
}

export function trackEvidenceToastViewClicked(input: EvidenceToastViewClickedEventProps) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceToastViewClicked,
      label: 'evidence_toast_view',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      chat_id: input.chat_id,
    },
  });
}

export function trackEvidenceUnsaveChatClicked(
  input: EvidenceUnsaveChatClickedEventProps
) {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: ScreenName.Evidence,
      action: ButtonAction.EvidenceUnsaveChatClicked,
      label: 'evidence_unsave_chat',
      evidence_location: input.evidence_location,
      region: input.region,
      is_evidence_pro: input.is_evidence_pro,
      is_team_member: input.is_team_member,
      chat_id: input.chat_id,
      click_source: input.click_source,
    },
  });
}
